"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  PlayCircle,
  FileText,
  Download,
  Radio,
  ClipboardList,
  CheckSquare,
  ChevronDown,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getLocalizedField,
  type Course,
  type CourseLesson,
} from "../../api";
import type { Locale } from "@/lib/i18n";
import type { EntitlementAccessScope } from "@/features/dashboard/types";
import type { CurriculumLesson, CurriculumModule } from "../../lib/curriculum-props";
import { resolveCurriculumLessonRowModel } from "../../lib/curriculum-gate";
import { resolveDripAvailability } from "../../lib/drip-access";
import { CourseShowcaseSectionHeader } from "./CourseShowcaseSectionHeader";

type HubTranslator = ReturnType<typeof useTranslations>;

type Props = {
  modules: CurriculumModule[];
  courseSlug: string;
  locale: Locale;
  /** From parent — library match for this course. */
  isOwned?: boolean;
  ownedAccessScope?: EntitlementAccessScope;
  completedLessonIds?: string[];
  coursePhases?: Course["phases"];
  /** Small uppercase label above "Course content" */
  headerEyebrow?: string;
  /** Enrollment instant — drip `days_after_enrollment` */
  enrollmentGrantedAt?: Date | null;
  /** From public course detail — empty-state copy */
  nextCohortStartAt?: string | null;
};

function getLessonIcon(type: CourseLesson["type"]) {
  switch (type) {
    case "video":
      return PlayCircle;
    case "text":
      return FileText;
    case "resource":
      return Download;
    case "live":
      return Radio;
    case "quiz":
      return CheckSquare;
    case "assignment":
      return ClipboardList;
    default:
      return FileText;
  }
}

function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatUnlockLabel(
  locale: Locale,
  t: HubTranslator,
  drip: ReturnType<typeof resolveDripAvailability>,
): string {
  if (!drip.blocked) return "";
  if (drip.scheduleUnknown) return t("dripScheduledRelease");
  if (drip.unlockAt) {
    const df = new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
      dateStyle: "medium",
    });
    return t("dripUnlocksOn", { date: df.format(drip.unlockAt) });
  }
  return t("dripScheduledRelease");
}

function computeModuleDripBadge(
  mod: CurriculumModule,
  opts: {
    courseSlug: string;
    locale: Locale;
    courseModules: CurriculumModule[];
    isOwned: boolean;
    accessScope: EntitlementAccessScope | undefined;
    completed: Set<string>;
    coursePhases: Course["phases"];
    enrollmentGrantedAt: Date | null;
    now: Date;
    t: HubTranslator;
  },
): string | null {
  if (!opts.isOwned || mod.lessons.length === 0) return null;

  const sortedLessons = [...mod.lessons].sort((a, b) => a.order - b.order);
  const eligible: CurriculumLesson[] = [];
  for (const lesson of sortedLessons) {
    const sansDrip = resolveCurriculumLessonRowModel({
      courseSlug: opts.courseSlug,
      locale: opts.locale,
      isOwned: opts.isOwned,
      accessScope: opts.accessScope,
      lesson,
      moduleId: mod._id,
      courseModules: opts.courseModules,
      coursePhases: opts.coursePhases,
      completedLessonIds: opts.completed,
      dripBlocked: false,
      dripHint: null,
    });
    if (sansDrip.linkHref) eligible.push(lesson);
  }

  if (eligible.length === 0) return null;

  let earliest: Date | null = null;
  for (const lesson of eligible) {
    const drip = resolveDripAvailability(lesson.dripRule, opts.now, opts.enrollmentGrantedAt);
    if (!drip.blocked) return null;
    if (drip.unlockAt && (!earliest || drip.unlockAt.getTime() < earliest.getTime())) {
      earliest = drip.unlockAt;
    }
  }

  if (earliest) {
    const df = new Intl.DateTimeFormat(opts.locale === "bn" ? "bn-BD" : "en-GB", {
      dateStyle: "medium",
    });
    return opts.t("moduleUnlocksShort", { date: df.format(earliest) });
  }
  return opts.t("moduleUnlocksTbd");
}

function LessonRow({
  lesson,
  courseSlug,
  locale,
  moduleId,
  courseModules,
  isOwned,
  accessScope,
  completed,
  coursePhases,
  enrollmentGrantedAt,
  t,
}: {
  lesson: CurriculumLesson;
  courseSlug: string;
  locale: Locale;
  moduleId: string;
  courseModules: CurriculumModule[];
  isOwned: boolean;
  accessScope: EntitlementAccessScope | undefined;
  completed: Set<string>;
  coursePhases: Course["phases"];
  enrollmentGrantedAt: Date | null;
  t: HubTranslator;
}) {
  const Icon = getLessonIcon(lesson.type);
  const title =
    getLocalizedField(lesson.title_en, lesson.title_bn, locale) ?? lesson.title_en;

  const now = new Date();
  const sansDrip = resolveCurriculumLessonRowModel({
    courseSlug,
    locale,
    isOwned,
    accessScope,
    lesson,
    moduleId,
    courseModules,
    coursePhases,
    completedLessonIds: completed,
    dripBlocked: false,
    dripHint: null,
  });

  const drip = resolveDripAvailability(lesson.dripRule, now, enrollmentGrantedAt);
  const dripBlocked = isOwned && Boolean(sansDrip.linkHref) && drip.blocked;
  const dripHint = formatUnlockLabel(locale, t, drip);

  const row = resolveCurriculumLessonRowModel({
    courseSlug,
    locale,
    isOwned,
    accessScope,
    lesson,
    moduleId,
    courseModules,
    coursePhases,
    completedLessonIds: completed,
    dripBlocked,
    dripHint: dripBlocked ? dripHint : null,
  });

  const inner = (
    <div
      className={cn(
        "group/row flex items-center gap-3 px-5 py-3 text-sm transition-colors duration-150",
        row.linkHref ? "cursor-pointer hover:bg-neutral-50" : "cursor-default",
        row.showFreePreviewBadge && "bg-primary/[0.03]",
      )}
      title={row.linkHref ? undefined : (row.hint ?? undefined)}
    >
      <Icon aria-hidden
        className={cn(
          "h-4 w-4 flex-shrink-0",
          row.showFreePreviewBadge
            ? "text-primary"
            : lesson.type === "live"
              ? "text-orange-500"
              : "text-neutral-400",
        )}
      />
      <span
        className={cn(
          "flex-1 leading-snug",
          row.showFreePreviewBadge
            ? "font-medium text-neutral-900"
            : !row.showLockIcon && row.linkHref
              ? "text-neutral-900"
              : "text-neutral-500",
        )}
      >
        {title}
      </span>
      <div className="flex flex-shrink-0 items-center gap-2">
        {row.mode === "owned_drip_locked" && dripHint && (
          <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-600">
            {dripHint}
          </span>
        )}
        {/*
          These rows are the only thing a logged-out visitor can actually open,
          so the affordance reads as a button rather than a caption.
        */}
        {row.showFreePreviewBadge && (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary transition-colors duration-150 group-hover/row:border-primary group-hover/row:bg-primary group-hover/row:text-white">
            <PlayCircle className="h-3 w-3" aria-hidden />
            {t("freePreviewBadge")}
          </span>
        )}
        {row.showLockIcon && (
          <Lock className="h-3.5 w-3.5 text-neutral-300" aria-hidden />
        )}
        {lesson.duration !== undefined && lesson.duration > 0 && (
          <span className="text-xs text-neutral-400">
            {formatDuration(lesson.duration)}
          </span>
        )}
      </div>
    </div>
  );

  if (row.linkHref) {
    return (
      <Link href={row.linkHref} title={row.hint ?? undefined}>
        {inner}
      </Link>
    );
  }
  return inner;
}

function ModuleRow({
  module: mod,
  courseSlug,
  locale,
  isExpanded,
  onToggle,
  courseModules,
  isOwned,
  accessScope,
  completed,
  coursePhases,
  enrollmentGrantedAt,
  t,
}: {
  module: CurriculumModule;
  courseSlug: string;
  locale: Locale;
  isExpanded: boolean;
  onToggle: () => void;
  courseModules: CurriculumModule[];
  isOwned: boolean;
  accessScope: EntitlementAccessScope | undefined;
  completed: Set<string>;
  coursePhases: Course["phases"];
  enrollmentGrantedAt: Date | null;
  t: HubTranslator;
}) {
  const title =
    getLocalizedField(mod.title_en, mod.title_bn, locale) ?? mod.title_en;
  const lessonCount = mod.lessons.length;
  const totalDuration = mod.lessons.reduce(
    (acc, l) => acc + (l.duration ?? 0),
    0,
  );
  const durationText = totalDuration > 0 ? formatDuration(totalDuration) : null;

  /** Public visitors get a reason to open the module before they open it. */
  const freeCount = isOwned ? 0 : mod.lessons.filter((l) => l.freePreview).length;

  const moduleBadge = computeModuleDripBadge(mod, {
    courseSlug,
    locale,
    courseModules,
    isOwned,
    accessScope,
    completed,
    coursePhases,
    enrollmentGrantedAt,
    now: new Date(),
    t,
  });

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 bg-white px-5 py-4 text-left transition-colors duration-150 hover:bg-neutral-50"
        aria-expanded={isExpanded}
      >
        <span className="min-w-0 text-[15px] font-semibold text-neutral-900">{title}</span>
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
          {freeCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
              <PlayCircle className="h-3 w-3" aria-hidden />
              {t("moduleFreePreviews", {
                // Bangla copy on this page uses Bangla numerals throughout.
                count: new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-GB").format(
                  freeCount,
                ),
              })}
            </span>
          )}
          {moduleBadge && (
            <span className="rounded-md bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-primary">
              {moduleBadge}
            </span>
          )}
          <span className="text-[13px] text-neutral-500">
            {lessonCount} lesson{lessonCount !== 1 ? "s" : ""}
            {durationText && ` · ${durationText}`}
          </span>
          <ChevronDown aria-hidden
            className={cn(
              "h-4 w-4 text-neutral-400 transition-transform duration-200",
              isExpanded && "rotate-180",
            )}
          />
        </div>
      </button>

      <div
        className={cn(
          "overflow-hidden transition-[max-height] duration-200 ease-out",
          isExpanded ? "max-h-[3000px]" : "max-h-0",
        )}
      >
        {mod.lessons.length > 0 && (
          <div className="divide-y divide-neutral-100 border-t border-neutral-100">
            {mod.lessons
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((lesson) => (
                <LessonRow
                  key={lesson._id}
                  lesson={lesson}
                  courseSlug={courseSlug}
                  locale={locale}
                  moduleId={mod._id}
                  courseModules={courseModules}
                  isOwned={isOwned}
                  accessScope={accessScope}
                  completed={completed}
                  coursePhases={coursePhases}
                  enrollmentGrantedAt={enrollmentGrantedAt}
                  t={t}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CourseCurriculumSection({
  modules,
  courseSlug,
  locale,
  isOwned = false,
  ownedAccessScope,
  completedLessonIds = [],
  coursePhases,
  headerEyebrow,
  enrollmentGrantedAt = null,
  nextCohortStartAt = null,
}: Props) {
  const t = useTranslations("courseHub");
  const completed = useMemo(
    () => new Set(completedLessonIds),
    [completedLessonIds],
  );

  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => a.order - b.order),
    [modules],
  );

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>();
    sortedModules.slice(0, 3).forEach((m) => s.add(m._id));
    return s;
  });

  const totalLessons = useMemo(
    () => modules.reduce((acc, m) => acc + m.lessons.length, 0),
    [modules],
  );

  const totalSeconds = useMemo(
    () =>
      modules.reduce(
        (acc, m) =>
          acc + m.lessons.reduce((la, l) => la + (l.duration ?? 0), 0),
        0,
      ),
    [modules],
  );

  const allExpanded = modules.length > 0 && expanded.size === modules.length;

  const toggleModule = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set());
    } else {
      setExpanded(new Set(modules.map((m) => m._id)));
    }
  };

  if (modules.length === 0) {
    if (!isOwned) return null;
    const cohortLine =
      nextCohortStartAt != null && nextCohortStartAt !== ""
        ? (() => {
            const df = new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
              dateStyle: "medium",
            });
            try {
              return t("emptyCurriculumWithDate", {
                date: df.format(new Date(nextCohortStartAt)),
              });
            } catch {
              return t("emptyCurriculumNoDate");
            }
          })()
        : t("emptyCurriculumNoDate");

    return (
      <section id="curriculum" className="scroll-mt-28">
        <CourseShowcaseSectionHeader
          eyebrow={headerEyebrow}
          title={t("curriculumTitle")}
          description={t("emptyCurriculumSummary")}
        />
        <div className="rounded-xl border border-neutral-200 bg-neutral-50/80 px-5 py-6 text-[14px] leading-relaxed text-neutral-700">
          {cohortLine}
        </div>
      </section>
    );
  }

  const durationDisplay =
    totalSeconds > 0
      ? (() => {
          const mins = Math.round(totalSeconds / 60);
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
        })()
      : null;

  const summaryLine = `${modules.length} module${modules.length !== 1 ? "s" : ""} · ${totalLessons} lesson${totalLessons !== 1 ? "s" : ""}${durationDisplay ? ` · ${durationDisplay} total` : ""}`;

  return (
    <section id="curriculum" className="scroll-mt-28">
      <CourseShowcaseSectionHeader
        eyebrow={headerEyebrow}
        title={t("curriculumTitle")}
        description={summaryLine}
        size="lg"
      />
      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={toggleAll}
          className="text-[14px] font-medium text-primary hover:underline"
        >
          {allExpanded ? t("collapseAll") : t("expandAll")}
        </button>
      </div>

      <div className="space-y-3">
        {sortedModules.map((mod) => (
          <ModuleRow
            key={mod._id}
            module={mod}
            courseSlug={courseSlug}
            locale={locale}
            isExpanded={expanded.has(mod._id)}
            onToggle={() => toggleModule(mod._id)}
            courseModules={modules}
            isOwned={isOwned}
            accessScope={ownedAccessScope}
            completed={completed}
            coursePhases={coursePhases}
            enrollmentGrantedAt={enrollmentGrantedAt}
            t={t}
          />
        ))}
      </div>
    </section>
  );
}
